import React, { useState } from 'react';
import { Menu, X, Search, ChevronDown, CheckCircle, ExternalLink, Ruler, Filter, Info } from 'lucide-react';

interface HeaderProps {
  onBrandChange?: (brand: string) => void;
  currentBrand?: string;
  onSearch?: (term: string) => void;
  onQuickFilter?: (type: 'rn' | 'dia' | 'noite') => void;
  onSizeChange?: (size: string) => void;
  onLineChange?: (line: string) => void;
  activeSize?: string;
  activeLine?: string;
}

const Header: React.FC<HeaderProps> = ({ 
  onBrandChange, 
  currentBrand = 'Pampers', 
  onSearch, 
  onQuickFilter,
  onSizeChange,
  onLineChange,
  activeSize = 'Todos',
  activeLine = 'Todos'
}) => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [searchValue, setSearchValue] = useState('');
  const [logoError, setLogoError] = useState(false);

  const handleBrandClick = (brand: string) => {
    onBrandChange?.(brand);
    setMobileMenuOpen(false);
  };

  const handleSearchSubmit = (e?: React.FormEvent) => {
    e?.preventDefault();
    onSearch?.(searchValue);
    setMobileMenuOpen(false);
  };

  const scrollToSection = (id: string) => {
    const el = document.getElementById(id);
    if (el) {
      const offset = 135; // Compensação ultra-otimizada para o header sticky
      const bodyRect = document.body.getBoundingClientRect().top;
      const elementRect = el.getBoundingClientRect().top;
      const elementPosition = elementRect - bodyRect;
      const offsetPosition = elementPosition - offset;
      window.scrollTo({ top: offsetPosition, behavior: 'smooth' });
    }
  };

  const sizes = ['RN', 'P', 'M', 'G', 'XG', 'XXG'];
  const brandLines = currentBrand === 'Pampers' 
    ? ['Premium Care', 'Confort Sec', 'Pants', 'Supersec']
    : ['Supreme Care', 'Tripla Proteção', 'Pants', 'Natural Care'];

  return (
    <div className="w-full font-sans sticky top-0 z-50 shadow-sticky-bar transition-all duration-300">
      {/* Top Bar - Ultra Compact Trust Label */}
      <div className="bg-store-dark text-white text-[9px] md:text-xs py-1 border-b border-white/5 relative z-50">
        <div className="max-w-7xl mx-auto px-4 flex justify-between items-center font-bold tracking-widest uppercase opacity-90">
          <span className="flex items-center gap-1.5"><CheckCircle size={10} className="text-store-accent"/> Links Verificados Amazon</span>
          <span className="hidden md:flex items-center gap-1.5"><ExternalLink size={10} className="text-store-accent"/> Preços Sincronizados</span>
          <span className="flex items-center gap-1.5"><CheckCircle size={10} className="text-store-accent"/> Curadoria Especializada</span>
        </div>
      </div>

      {/* Main Header Area - Reduced Padding */}
      <header className="bg-white relative z-40">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-1.5 md:py-2.5">
          <div className="flex items-center justify-between gap-3 md:gap-8">
            
            {/* Logo - Slightly Smaller for better fit */}
            <a href="/" className="flex-shrink-0 select-none block group">
               {!logoError ? (
                 <img 
                   src="/assets/logo-fraldatech.webp" 
                   alt="FraldaTech" 
                   className="h-[24px] md:h-[34px] w-auto object-contain transition-transform group-hover:scale-105"
                   onError={() => setLogoError(true)}
                 />
               ) : (
                 <div className="flex flex-col justify-center items-start leading-none h-[24px] md:h-[34px]">
                    <span className="font-black text-lg md:text-2xl tracking-tighter text-store-purple">
                      Fralda<span className="text-store-accent">Tech</span>
                    </span>
                    <span className="text-[6px] md:text-[8px] font-black tracking-[0.2em] text-store-purple uppercase">Especialistas</span>
                 </div>
               )}
            </a>

            {/* Search Area */}
            <div className="hidden lg:flex flex-1 flex-col max-w-xl group">
              <form className="relative w-full" onSubmit={handleSearchSubmit}>
                <input 
                  type="text" 
                  value={searchValue}
                  onChange={(e) => setSearchValue(e.target.value)}
                  placeholder="Ex: Pampers G 128 unidades..." 
                  className="w-full pl-5 pr-14 py-2 rounded-xl text-sm text-gray-700 bg-gray-50 border border-gray-200 focus:bg-white focus:outline-none focus:ring-2 focus:ring-store-purple/30 transition-all"
                />
                <button type="submit" className="absolute right-1 top-1 bottom-1 px-4 bg-store-purple hover:bg-store-dark text-white rounded-lg transition-colors flex items-center justify-center">
                    <Search size={16} />
                </button>
              </form>
            </div>

            {/* Mobile Actions */}
            <div className="flex lg:hidden">
              <button 
                className="text-store-purple p-2 hover:bg-gray-100 rounded-lg"
                onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              >
                <Menu size={24} />
              </button>
            </div>
          </div>
          
          {/* Disclaimer - Enhanced Readability */}
          <div className="hidden lg:block mt-1 opacity-90">
            <p className="text-[10px] text-gray-500 font-bold flex items-center gap-1 tracking-tight">
              <Info size={11} className="text-store-purple" /> Ao clicar em VER PREÇO, você será redirecionado para a Amazon para finalizar a compra com total segurança.
            </p>
          </div>
          
          <div className="lg:hidden mt-2">
            <form className="relative" onSubmit={handleSearchSubmit}>
                <input 
                  type="text" 
                  value={searchValue}
                  onChange={(e) => setSearchValue(e.target.value)}
                  placeholder="Busque por tamanho ou linha..." 
                  className="w-full pl-4 pr-12 py-1.5 rounded-lg text-sm border border-gray-200 bg-gray-50"
                />
                <button type="submit" className="absolute right-1 top-1 bottom-1 w-9 bg-store-purple rounded-md text-white flex items-center justify-center">
                  <Search size={14} />
                </button>
            </form>
            <p className="text-[9px] text-gray-600 mt-1 text-center font-black">Links verificados levam à Amazon Brasil.</p>
          </div>
        </div>

        {/* Quick Shortcuts Bar - ULTRA COMPACT & STICKY */}
        <div className="bg-white border-t border-gray-50 py-1.5 shadow-sm">
           <div className="max-w-7xl mx-auto px-4 overflow-x-auto hide-scrollbar">
              <div className="flex items-center gap-2 md:gap-4 min-w-max">
                 
                 {/* Brands Group */}
                 <div className="flex items-center gap-1 bg-gray-100 p-0.5 rounded-lg border border-gray-200">
                   {['Pampers', 'Huggies', 'Babysec', 'Mamypoko'].map(b => (
                     <button 
                       key={b}
                       onClick={() => handleBrandClick(b)}
                       className={`px-2.5 py-1 rounded-md text-[9px] md:text-[10px] font-black uppercase tracking-wider transition-all ${currentBrand === b ? 'bg-white text-store-purple shadow-sm' : 'text-gray-400 hover:text-gray-600'}`}
                     >
                       {b}
                     </button>
                   ))}
                 </div>

                 <div className="h-4 w-px bg-gray-200"></div>

                 {/* Sizes Group */}
                 <div className="flex items-center gap-1.5">
                   <span className="text-[8px] font-black text-gray-400 uppercase">Tamanhos:</span>
                   {sizes.map(s => (
                     <button 
                       key={s}
                       onClick={() => { onSizeChange?.(s); scrollToSection('categorias'); }}
                       className={`px-2.5 py-1 border rounded-lg text-[9px] md:text-xs font-bold transition-all ${activeSize === s ? 'bg-store-purple text-white border-store-purple' : 'bg-white border-gray-200 text-gray-600'}`}
                     >
                       {s}
                     </button>
                   ))}
                 </div>

                 <div className="h-4 w-px bg-gray-200"></div>

                 {/* Lines Group */}
                 <div className="flex items-center gap-1.5">
                   <span className="text-[8px] font-black text-gray-400 uppercase">Linhas:</span>
                   {brandLines.map(l => (
                     <button 
                       key={l}
                       onClick={() => { onLineChange?.(l); scrollToSection('categorias'); }}
                       className={`px-2.5 py-1 border rounded-lg text-[9px] md:text-xs font-bold transition-all whitespace-nowrap ${activeLine === l ? 'bg-store-purple text-white border-store-purple' : 'bg-white border-gray-200 text-gray-600'}`}
                     >
                       {l}
                     </button>
                   ))}
                 </div>
              </div>
           </div>
        </div>
      </header>
    </div>
  );
};

export default Header;